import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { isAfter, isBefore } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import i18n from '../../i18n';
import { getValidationSchema } from '../../util/helpers/getValidationSchema';
import { FormContainer, FormWrap } from '../FormContainer/FormContainer';
import InputText, { ErrorMessage } from '../Fields/InputText';
import Select from '../Fields/Select';
import OnBoardingCta from '../OnBoarding/OnBoardingCta';
import SaveAndCloseModal from '../Modals/SaveAndCloseModal/SaveAndCloseModal';
import useStepPost from '../../hooks/useStepPost';
import Paragraph from '../Paragraph/Paragraph';
import useIdentificationInfoDispatch from '../../hooks/useIdentificationInfoDispatch';
import { identifcationVerificationSelector } from '../../store/selectors/identificationVerificationSelectors';
import {
  selectClientCountries,
  selectClientStates,
} from '../../store/selectors/commonSelectors';
import { getIdentificationTypes } from '../../store/actions/identificationVerification/identificationVerificationActions';
import {
  getClientCountries,
  getClientStates,
} from '../../store/actions/common/commonActions';
import { selectApplication } from '../../store/selectors/applicationSelectors';
import { getDaysPerMonth } from '../../util/helpers/datesHelpers';
import InputNewText from '../Fields/NewInputText';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_VIEW, ACCOUNT_STEP_COMPLETE } from '../../util/gtmEvents';
import { ACCOUNT_TYPE_GTM, BROKER_CODE } from '../../constants/sessionStorage';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { useApplicantData } from '../../hooks/useApplicantData';

const DateFieldsWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 32px;
  flex-wrap: nowrap;
  margin-top: 20px;
`;

const DateContainer = styled.div`
  flex: 1; /* Ensures both sections take equal space */
`;

const DateFieldsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(4.1rem, 1fr));
  gap: 10px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: repeat(
      3,
      minmax(2.613rem, 1fr)
    ); /* Smaller inputs */
    grid-gap: 1px;
  }
`;

// eslint-disable-next-line prettier/prettier
const ExpirationDateContainer = styled.div``;

// eslint-disable-next-line prettier/prettier
const IssueDateContainer = styled.div``;

const IDDetailsForm = ({ nextPath, onModalClose, setEventType, formId }) => {
  const { t } = useTranslation();
  const issuedDateRefs = {
    month: useRef(),
    day: useRef(),
    year: useRef(),
    hidden: useRef(),
  };
  const expiryDateRefs = {
    month: useRef(),
    day: useRef(),
    year: useRef(),
    hidden: useRef(),
  };
  const { BrokerCode } = useSelector(selectSettings);
  const dispatch = useDispatch();
  const { onSuccess } = useStepPost({ nextPath, onModalClose });
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);
  const ApplicantData = useApplicantData();
  const [countriesOptions, setCountriesOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const states = useSelector(selectClientStates);
  const [issueDate, setIssueDate] = useState();
  const [expiryDate, setExpiryDate] = useState();
  const [issueDateError, setIssueDateError] = useState('');
  const [generalerror, setGeneralError] = useState('');
  const [expiryDateError, setExpiryDateError] = useState('');
  const [iso2code, setIso2Code] = useState('');
  const [iso3code, setIso3Code] = useState('');
  const countries = useSelector(selectClientCountries);
  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);
  const [selectedIdentificationType, setSelectedIdentificationType] = useState(
    {},
  );
  const [formDefaultValues, setFormDefaultValues] = useState({});
  const { identifcationInfoDispatch } = useIdentificationInfoDispatch();
  const caption = selectedIdentificationType.caption || '';

  let stateValidation = Yup.string().nullable();
  let countryValidation = Yup.string().nullable();

  const [brokerCode, setBrokerCode] = useState('');
  useEffect(() => {
    if (BrokerCode) {
      setBrokerCode(BrokerCode);
    } else {
      const bCode = retrieveFromSessionStorage(BROKER_CODE);
      setBrokerCode(bCode);
    }
  }, [BrokerCode]);

  if (caption !== 'Passport') {
    stateValidation = stateValidation.required(
      i18n.t('fields.errors.validation.stateRequired'),
    );
  }
  if (caption === 'Passport') {
    countryValidation = countryValidation.required(
      i18n.t('fields.errors.validation.issuedByRequired'),
    );
  }

  const { identificationTypesSources } = useSelector(
    identifcationVerificationSelector,
  );
  const Application = useSelector(selectApplication);
  const formValidationSchema = Yup.object({
    ...getValidationSchema({
      fields: ['IDType', 'IDNumber'],
    }),
    IssuedBy: countryValidation,
    State: stateValidation,
    Month: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.month`))
      .min(1, i18n.t(`fields.errors.validation.minMonth`))
      .max(12, i18n.t(`fields.errors.validation.maxMonth`))
      .required(),
    Year: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.year`))
      .required()
      .test(
        'len',
        i18n.t(`fields.errors.validation.minYear`),
        (val) => val && val.toString().length === 4,
      ),
    Day: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.day`))
      .min(1, i18n.t(`fields.errors.validation.minDay`))
      .when(['Month', 'Year'], (Month, Year, schema) =>
        Month && Month > 0 && Month < 13 && Year
          ? schema.max(
              getDaysPerMonth(
                Month.toString().padStart(2, '0'),
                Year.toString().padStart(2, '0'),
              ).length,
              i18n.t(`fields.errors.validation.maxDay`),
            )
          : schema.max(30, i18n.t(`fields.errors.validation.maxDay`)),
      )
      .required(),
    ExpiryMonth: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.month`))
      .min(1, i18n.t(`fields.errors.validation.minMonth`))
      .max(12, i18n.t(`fields.errors.validation.maxMonth`))
      .required(),
    ExpiryYear: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.year`))
      .required()
      .test(
        'len',
        i18n.t(`fields.errors.validation.minYear`),
        (val) => val && val.toString().length === 4,
      ),
    ExpiryDay: Yup.number()
      .typeError(i18n.t(`fields.errors.validation.day`))
      .min(1, i18n.t(`fields.errors.validation.minDay`))
      .when(['ExpiryMonth', 'ExpiryYear'], (Month, Year, schema) =>
        Month && Month > 0 && Month < 13 && Year
          ? schema.max(
              getDaysPerMonth(
                Month.toString().padStart(2, '0'),
                Year.toString().padStart(2, '0'),
              ).length,
              i18n.t(`fields.errors.validation.maxDay`),
            )
          : schema.max(30, i18n.t(`fields.errors.validation.maxDay`)),
      )
      .required(),
  });

  const {
    handleSubmit,
    control,
    watch,
    setError,
    reset,
    setValue,
    trigger,
  } = useForm({
    resolver: yupResolver(formValidationSchema),
    formDefaultValues,
    mode: 'onChange',
  });

  const month = watch('Month');
  const year = watch('Year');

  const expirymonth = watch('ExpiryMonth');
  const expiryyear = watch('ExpiryYear');

  useEffect(() => {
    if (month && year) {
      trigger('Day');
    }
  }, [month, year, trigger]);

  useEffect(() => {
    if (expirymonth && expiryyear) {
      trigger('ExpiryDay');
    }
  }, [expirymonth, expiryyear, trigger]);

  useEffect(() => {
    if (Application && !isEmpty(Application)) {
      const {
        IRAApplication: {
          CustomerDueDiligenceResponse: { IRAIdentificationInfoResponse },
        },
      } = Application;
      const existingIdentificationType =
        IRAIdentificationInfoResponse?.IdentificationType;
      const existingIDNumber = IRAIdentificationInfoResponse?.IDNumber;
      const existingIssueDate = IRAIdentificationInfoResponse?.IssueDate
        ? new Date(IRAIdentificationInfoResponse.IssueDate)
        : null;
      const existingExpirationDate = IRAIdentificationInfoResponse?.ExpirationDate
        ? new Date(IRAIdentificationInfoResponse.ExpirationDate)
        : null;
      const IssuedByCountry = IRAIdentificationInfoResponse?.IssuedByCountry;

      const IssuedByState = IRAIdentificationInfoResponse?.IssuedByState;
      const issueDay = existingIssueDate
        ? String(existingIssueDate.getDate()).padStart(2, '0')
        : '';
      const issueMonth = existingIssueDate
        ? String(existingIssueDate.getMonth() + 1).padStart(2, '0')
        : ''; // Months are zero-based
      const issueYear = existingIssueDate
        ? String(existingIssueDate.getFullYear())
        : '';

      const expiryDay = existingExpirationDate
        ? String(existingExpirationDate.getDate()).padStart(2, '0')
        : '';
      const expiryMonth = existingExpirationDate
        ? String(existingExpirationDate.getMonth() + 1).padStart(2, '0')
        : '';
      const expiryYear = existingExpirationDate
        ? String(existingExpirationDate.getFullYear())
        : '';

      const defaultValues = {
        IDType: existingIdentificationType,
        IDNumber: existingIDNumber,
        IssueDate: existingIssueDate,
        ExpiryDate: existingExpirationDate,
        IssuedBy: IssuedByCountry,
        State: IssuedByState,
        Month: issueMonth ? Number(issueMonth) : null,
        Day: issueDay ? Number(issueDay) : null,
        Year: issueYear ? Number(issueYear) : null,
        ExpiryMonth: expiryMonth ? Number(expiryMonth) : null,
        ExpiryDay: expiryDay ? Number(expiryDay) : null,
        ExpiryYear: expiryYear ? Number(expiryYear) : null,
      };

      setFormDefaultValues(defaultValues);
      setIso2Code(IRAIdentificationInfoResponse.IssuedByState);
      setIso3Code(IRAIdentificationInfoResponse.IssuedByCountry || '');
      if (issuedDateRefs.month.current)
        issuedDateRefs.month.current.value = issueMonth;
      if (issuedDateRefs.day.current)
        issuedDateRefs.day.current.value = issueDay;
      if (issuedDateRefs.year.current)
        issuedDateRefs.year.current.value = issueYear;

      if (expiryDateRefs.month.current)
        expiryDateRefs.month.current.value = expiryMonth;
      if (expiryDateRefs.day.current)
        expiryDateRefs.day.current.value = expiryDay;
      if (expiryDateRefs.year.current)
        expiryDateRefs.year.current.value = expiryYear;

      const expirydate = new Date(`${expiryYear}-${expiryMonth}-${expiryDay}`);
      setExpiryDate(expirydate);

      const issuedate = new Date(`${issueYear}-${issueMonth}-${issueDay}`);
      setIssueDate(issuedate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Application]);
  useEffect(() => {
    if (identificationTypesSources.length === 0) {
      dispatch(getIdentificationTypes());
    }
  }, [identificationTypesSources, dispatch]);

  useEffect(() => {
    if (states.length === 0) {
      dispatch(getClientStates());
    }
  }, [states, dispatch]);
  useEffect(() => {
    if (countries.length === 0) {
      dispatch(getClientCountries());
    }
  }, [countries, dispatch]);

  // eslint-disable-next-line no-unused-vars
  const handleIssueDateCompletion = () => {
    const day = issuedDateRefs.day.current?.value;
    const month = issuedDateRefs.month.current?.value;
    const year = issuedDateRefs.year.current?.value;
    if (!day || !month || !year || year.length !== 4) {
      return;
    }

    const date = new Date(`${year}-${month}-${day}`);
    const today = new Date();

    if (isAfter(date, today)) {
      setIssueDateError(t('fields.errors.validation.issueDateInvalid'));
      setIssueDate();
    } else {
      setIssueDateError('');
      setIssueDate(date);
    }
  };

  const handleExpiryDateCompletion = () => {
    const day = expiryDateRefs.day.current?.value;
    const month = expiryDateRefs.month.current?.value;
    const year = expiryDateRefs.year.current?.value;

    if (!day || !month || !year || year.length !== 4) {
      return;
    }

    const date = new Date(`${year}-${month}-${day}`);
    const today = new Date();

    if (isBefore(date, today)) {
      setExpiryDateError(t('fields.errors.validation.expiryDateInvalid'));
      setExpiryDate();
    } else {
      setExpiryDateError('');
      setExpiryDate(date);
    }
  };
  const isPersonAddressEmpty = (address) =>
    address && Object.values(address).every((value) => value === null);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Legal Entity Details',
      accountType: accountGtmType,
      ...(isEmpty(ApplicantData?.PersonName?.FirstName)
        ? {}
        : { accountName: ApplicantData?.PersonName }),
      ...(isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
        ? {}
        : { accountDateOfBirth: ApplicantData?.PersonBirthday }),
      ...(isEmpty(ApplicantData?.Phone?.LocalNumber)
        ? {}
        : { accountPhone: ApplicantData?.Phone }),
      ...(isPersonAddressEmpty(ApplicantData?.PersonAddress)
        ? {}
        : { accountAddress: ApplicantData?.PersonAddress }),
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Legal Entity Details',
      accountType: accountGtmType,
      ...(isEmpty(ApplicantData?.PersonName?.FirstName)
        ? {}
        : { accountName: ApplicantData?.PersonName }),
      ...(isEmpty(ApplicantData?.PersonBirthday?.DateOfBirth)
        ? {}
        : { accountDateOfBirth: ApplicantData?.PersonBirthday }),
      ...(isEmpty(ApplicantData?.Phone?.LocalNumber)
        ? {}
        : { accountPhone: ApplicantData?.Phone }),
      ...(isPersonAddressEmpty(ApplicantData?.PersonAddress)
        ? {}
        : { accountAddress: ApplicantData?.PersonAddress }),
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };
  const onRequestSuccess = () => {
    handleGtmEvent();
    onSuccess();
  };

  useEffect(() => {
    if (countries && !isEmpty(countries)) {
      const COUNTRY_OPTIONS = [
        ...countries.map((country) => ({
          value: country.Iso3Code,
          label: country.Caption,
          dataCy: 'menu-item-country',
        })),
      ];
      setCountriesOptions(COUNTRY_OPTIONS);
    }
  }, [countries]);

  useEffect(() => {
    if (states && !isEmpty(states)) {
      const STATE_OPTIONS = [
        ...states.map((state) => ({
          value: state.Iso2Code,
          label: state.Caption,
          dataCy: 'menu-item-state',
        })),
      ];
      setStateOptions(STATE_OPTIONS);
    }
  }, [states]);
  const getIdentificationCaptionAndRendering = (id) => {
    if (!id) {
      return {};
    }
    const item = identificationTypesSources?.find(
      (type) => type.IdentificationTypeID.toString() === id.toString(),
    );
    return item ? { id: id.toString(), caption: item.Caption } : {};
  };

  // eslint-disable-next-line no-unused-vars
  const IDType = watch('IDType');

  useEffect(() => {
    if (Application && !isEmpty(Application)) {
      reset({ ...formDefaultValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Application, reset]);

  useEffect(() => {
    const initialValue = getIdentificationCaptionAndRendering(IDType);
    setSelectedIdentificationType(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IDType]);

  const clearform = () => {
    setValue('IDNumber', '');
    setValue('Month', '');
    setValue('Day', '');
    setValue('Year', '');
    setValue('ExpiryMonth', '');
    setValue('ExpiryDay', '');
    setValue('ExpiryYear', '');
    setValue('IssuedBy', '');
    setValue('State', '');
    if (issuedDateRefs.month.current) issuedDateRefs.month.current.value = '';
    if (issuedDateRefs.day.current) issuedDateRefs.day.current.value = '';
    if (issuedDateRefs.year.current) issuedDateRefs.year.current.value = '';

    if (expiryDateRefs.month.current) expiryDateRefs.month.current.value = '';
    if (expiryDateRefs.day.current) expiryDateRefs.day.current.value = '';
    if (expiryDateRefs.year.current) expiryDateRefs.year.current.value = '';
    setIssueDateError('');
    setIssueDate(undefined);
    setExpiryDateError('');
    setExpiryDate(undefined);
    setIso2Code('');
    setIso3Code('');
  };

  const getIssuedByDefaultValue = (caption, dataArray, searchParam) => {
    if (caption === 'Passport') {
      return dataArray.find((item) => item.value === searchParam) || {};
    }
    return dataArray.find((item) => item.value === searchParam) || {};
  };

  const onSubmit = ({ IDType, IDNumber }) => {
    if (expiryDateError || issueDateError) {
      return;
    }
    const transformedPayload = {
      IdentificationType: IDType ?? 0,
      IDNumber: IDNumber || 'string',
      // IssueDate: issueDate ? issueDate.toISOString() : '',
      // ExpirationDate: expiryDate ? expiryDate.toISOString() : '',
      IssuedByCountry: caption !== 'Passport' ? null : iso3code || 'string',
      IssuedByState: caption === 'Passport' ? null : iso2code || 'string',
    };

    const transformedFormDefaultValues = {
      IdentificationType: formDefaultValues.IDType,
      IDNumber: formDefaultValues.IDNumber || 'string',
      // IssueDate: formDefaultValues.IssueDate
      //   ? formDefaultValues.IssueDate.toISOString()
      //   : '',
      // ExpirationDate: formDefaultValues.ExpiryDate
      //   ? formDefaultValues.ExpiryDate.toISOString()
      //   : '',
      IssuedByCountry: formDefaultValues.IssuedBy,
      IssuedByState: formDefaultValues.State,
    };
    identifcationInfoDispatch({
      data: transformedPayload,
      defaultValues: transformedFormDefaultValues,
      dates: {
        existingIssueDate: formDefaultValues?.IssueDate,
        changedIssueDate: issueDate,
        existingExpiryDate: formDefaultValues?.ExpiryDate,
        changedExpiryDate: expiryDate,
      },
      onSuccess: onRequestSuccess,
      onError: (error) => {
        if (error.field === 'general') {
          setGeneralError(error.message);
        } else {
          setError(error.field, {
            type: error.type,
            message: error.message,
          });
        }
      },
    });
  };
  return (
    <FormWrap id={formId} onSubmit={handleSubmit(onSubmit)}>
      <FormContainer>
        <Controller
          control={control}
          render={({ field, fieldState }) => {
            const initialValue = field.value
              ? {
                  value: field.value.toString(),
                  label: getIdentificationCaptionAndRendering(field.value)
                    .caption,
                }
              : {};
            return (
              <Select
                label={t('secureIdentityDetails.titleIDType')}
                options={
                  identificationTypesSources &&
                  identificationTypesSources.map(
                    ({ IdentificationTypeID, Caption }) => ({
                      value: IdentificationTypeID.toString(),
                      label: Caption,
                    }),
                  )
                }
                {...field}
                {...fieldState}
                value={initialValue}
                minHeight={brokerCode === 'HAA' ? 46 : 43.5}
                onChange={(e) => {
                  const newIDType = e.value;
                  field.onChange(e.value);
                  setSelectedIdentificationType({
                    id: e.value,
                    caption: e.label,
                  });

                  if (formDefaultValues?.IDType) {
                    if (formDefaultValues?.IDType.toString() === newIDType) {
                      reset({ ...formDefaultValues });
                      setIssueDate(formDefaultValues.IssueDate || undefined);
                      setExpiryDate(formDefaultValues.ExpiryDate || undefined);
                      setIso2Code(formDefaultValues.State);
                      setIso3Code(formDefaultValues.IssuedBy || '');
                    } else {
                      clearform();
                    }
                  } else {
                    clearform();
                  }
                }}
              />
            );
          }}
          name="IDType"
        />
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <InputText
              height={brokerCode === 'HAA' ? 46 : 43.5}
              {...field}
              {...fieldState}
              label={t('secureIdentityDetails.titleIDNumber')}
            />
          )}
          name="IDNumber"
        />
      </FormContainer>
      <>
        <DateFieldsWrapper>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: window.innerWidth < 768 ? '0.313rem' : '32px',
            }}
          >
            {/* Issued Date Section */}
            <DateContainer>
              <IssueDateContainer>
                <Paragraph variant="fieldHeader" fontWeight={600}>
                  {t('secureIdentityDetails.titleIssueDate')}
                </Paragraph>
                <DateFieldsContainer>
                  <Controller
                    control={control}
                    render={({ field, fieldState }) => (
                      <InputNewText
                        label="MM"
                        {...field}
                        {...fieldState}
                        type="number"
                        height={BrokerCode === 'GS' ? '43.5px' : '46px'}
                        autocomplete="bday-month"
                        placeholder="MM"
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 2);
                          if (e.target.value.length === 2) {
                            return issuedDateRefs.day.current?.focus();
                          }
                        }}
                        onBlur={handleIssueDateCompletion}
                        ref={issuedDateRefs.month}
                        data-cy="input-month"
                      />
                    )}
                    name="Month"
                  />
                  <Controller
                    control={control}
                    render={({ field, fieldState }) => (
                      <InputNewText
                        label="DD"
                        {...field}
                        {...fieldState}
                        type="number"
                        height={BrokerCode === 'GS' ? '43.5px' : '46px'}
                        autocomplete="bday-day"
                        placeholder="DD"
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 2);
                          if (e.target.value.length === 2) {
                            return issuedDateRefs.year.current?.focus();
                          }
                        }}
                        onBlur={handleIssueDateCompletion}
                        ref={issuedDateRefs.day}
                        data-cy="input-day"
                      />
                    )}
                    name="Day"
                  />
                  <Controller
                    control={control}
                    render={({ field, fieldState }) => (
                      <InputNewText
                        label="YYYY"
                        {...field}
                        {...fieldState}
                        type="number"
                        height={BrokerCode === 'GS' ? '43.5px' : '46px'}
                        autocomplete="bday-year"
                        placeholder="YYYY"
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 4);
                          if (e.target.value.length === 4) {
                            return issuedDateRefs.hidden.current?.focus();
                          }
                        }}
                        onBlur={handleIssueDateCompletion}
                        ref={issuedDateRefs.year}
                        data-cy="input-year"
                      />
                    )}
                    name="Year"
                  />
                  <input
                    type="text"
                    style={{
                      opacity: 0,
                      cursor: 'context-menu',
                      width: '0px',
                      height: '0px',
                    }}
                    ref={issuedDateRefs.hidden}
                  />
                </DateFieldsContainer>
                {issueDateError && (
                  <ErrorMessage>{issueDateError}</ErrorMessage>
                )}
              </IssueDateContainer>
            </DateContainer>

            {/* Expiration Date Section */}
            <DateContainer>
              <ExpirationDateContainer>
                <Paragraph variant="fieldHeader" fontWeight={600}>
                  {t('secureIdentityDetails.titleExpirationDate')}
                </Paragraph>
                <DateFieldsContainer>
                  <Controller
                    control={control}
                    render={({ field, fieldState }) => (
                      <InputNewText
                        label="MM"
                        {...field}
                        {...fieldState}
                        type="number"
                        height={BrokerCode === 'GS' ? '43.5px' : '46px'}
                        autocomplete="bday-month"
                        placeholder="MM"
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 2);
                          if (e.target.value.length === 2) {
                            return expiryDateRefs.day.current?.focus();
                          }
                        }}
                        onBlur={handleExpiryDateCompletion}
                        ref={expiryDateRefs.month}
                        data-cy="input-month"
                      />
                    )}
                    name="ExpiryMonth"
                  />
                  <Controller
                    control={control}
                    render={({ field, fieldState }) => (
                      <InputNewText
                        label="DD"
                        {...field}
                        {...fieldState}
                        type="number"
                        height={BrokerCode === 'GS' ? '43.5px' : '46px'}
                        autocomplete="bday-day"
                        placeholder="DD"
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 2);
                          if (e.target.value.length === 2) {
                            return expiryDateRefs.year.current?.focus();
                          }
                        }}
                        onBlur={handleExpiryDateCompletion}
                        ref={expiryDateRefs.day}
                        data-cy="input-day"
                      />
                    )}
                    name="ExpiryDay"
                  />
                  <Controller
                    control={control}
                    render={({ field, fieldState }) => (
                      <InputNewText
                        label="YYYY"
                        {...field}
                        {...fieldState}
                        type="number"
                        height={BrokerCode === 'GS' ? '43.5px' : '46px'}
                        autocomplete="bday-year"
                        placeholder="YYYY"
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 4);
                          if (e.target.value.length === 4) {
                            return expiryDateRefs.hidden.current?.focus();
                          }
                        }}
                        onBlur={handleExpiryDateCompletion}
                        ref={expiryDateRefs.year}
                        data-cy="input-year"
                      />
                    )}
                    name="ExpiryYear"
                  />
                  <input
                    type="text"
                    style={{
                      opacity: 0,
                      cursor: 'context-menu',
                      width: '0px',
                      height: '0px',
                    }}
                    ref={expiryDateRefs.hidden}
                  />
                </DateFieldsContainer>
                {expiryDateError && (
                  <ErrorMessage>{expiryDateError}</ErrorMessage>
                )}
              </ExpirationDateContainer>
            </DateContainer>
          </div>
        </DateFieldsWrapper>
      </>

      {(caption === 'US Driver`s License' || caption === 'US State ID') && (
        <FormContainer mt={22}>
          <Controller
            control={control}
            name="State"
            render={({ field, fieldState }) => {
              const initialValue = getIssuedByDefaultValue(
                caption,
                stateOptions,
                field.value,
              );
              return (
                <Select
                  label={t('secureIdentityDetails.titleIssuedBy')}
                  options={stateOptions}
                  isSearchable={false}
                  {...field}
                  {...fieldState}
                  value={initialValue}
                  onChange={(e) => {
                    setIso2Code(e.value);
                    field.onChange(e.value);
                  }}
                  minHeight={brokerCode === 'HAA' ? 46 : 43.5}
                  data-cy="button-toggle-state"
                />
              );
            }}
          />
        </FormContainer>
      )}

      {caption === 'Passport' && (
        <FormContainer mt={22}>
          {/* Passport Form */}
          <Controller
            control={control}
            name="IssuedBy"
            render={({ field, fieldState }) => {
              const initialValue = getIssuedByDefaultValue(
                caption,
                countriesOptions,
                field.value,
              );

              return (
                <Select
                  label={t('secureIdentityDetails.titleIssuedBy')}
                  options={countriesOptions}
                  {...field}
                  {...fieldState}
                  value={initialValue}
                  onChange={(e) => {
                    setIso3Code(e.value);
                    field.onChange(e.value);
                  }}
                  data-cy="button-toggle-country"
                  minHeight={brokerCode === 'HAA' ? 46 : 43.5}
                />
              );
            }}
          />
        </FormContainer>
      )}
      {generalerror && generalerror && (
        <div style={{ marginTop: '10px' }}>
          <ErrorMessage>{generalerror}</ErrorMessage>
        </div>
      )}
      <OnBoardingCta
        type="submit"
        onClick={() => {
          setEventType('Form');
        }}
        title={nextPath ? t('continue') : t('update')}
        buttonProps={{
          'data-cy': nextPath ? 'button-continue' : 'button-update',
        }}
      />
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
          // hasError={hasError}
          onSuccess={onSuccess}
        />
      )}
    </FormWrap>
  );
};

IDDetailsForm.propTypes = {
  nextPath: PropTypes.string,
  applicantType: PropTypes.string,
  onModalClose: PropTypes.func,
  eventType: PropTypes.string,
  setEventType: PropTypes.func,
  formId: PropTypes.string,
};

export default IDDetailsForm;
